// @ts-strict-ignore
import {
  AllTab,
  FilterResourceType,
  FilterValue,
  MenuItemType,
  SpecialActions,
  SpecialControl,
  StagedItemType,
} from 'common/query-builder/query-block/properties-menu/types';
import {DATASET_PROPERTY_NAME, PropertyType, ResourceType} from 'common/report/constants';
import {
  OrderedStandardTimeUnits,
  TimeUnit,
  TimeUnitLabels,
  TimeUnitLabelTranslatable,
} from 'common/widgets/date-range-picker/models/types';
import {SpecialTimeUnit} from 'common/types/reports/bookmark';
import {pluralize} from 'common/util/string';
import {SectionItem} from 'common/components/items-menu/types';

import {PropertySectionItem, SectionItemProperty} from './item-menu-types';

export const EXTRA_PROPERTY_DEFINITIONS_BY_TYPE = {
  [ResourceType.Event]: [
    {
      name: `$dataset`,
      displayName: `Project Name`,
      description: `Project Name is a virtual property that you can use to breakdown or filter your data based on the Project ID in which this event/property is tracked. [More about Rollup Projects](https://mixpanel.app.box.com/v/rollup-projects-beta)`,
      type: PropertyType.String,
      resourceType: ResourceType.Event,
    },
  ],
  [ResourceType.User]: [
    {
      name: `$mp_first_event_time`,
      displayName: ({t}) => t(`query-builder.items.first-seen.name`),
      description: ({t}) => t(`query-builder.items.first-seen.description`),
      type: PropertyType.Datetime,
      resourceType: ResourceType.User,
    },
  ],
};

export const TOP_PEOPLE: PropertySectionItem<string> = {
  type: null,
  propertyDefaultType: null,
  hasCaret: false,
  hasPill: false,
  icon: `profile`,
  isDisabled: false,
  label: ({t}) => t(`query-builder.metrics.all-profiles`),
  value: `$all_people`,
  resourceType: ResourceType.User,
  menuItemType: MenuItemType.UserProperties,
};

export const TOP_PATHS: PropertySectionItem<string> = {
  type: PropertyType.NonProperty,
  propertyDefaultType: PropertyType.NonProperty,
  hasCaret: false,
  hasPill: false,
  icon: `top-paths`,
  isDisabled: false,
  label: `Top paths`,
  value: `$paths`,
  resourceType: ResourceType.Other,
  menuItemType: MenuItemType.Other,
};

export const BEHAVIOR_FREQUENCY: PropertySectionItem<string> = Object.freeze({
  type: PropertyType.Number,
  propertyDefaultType: PropertyType.Number,
  caretStyle: `caret`,
  hasCaret: true,
  hasPill: false,
  icon: `type-custom-number`,
  isDisabled: false,
  label: `Frequency per User...`,
  dynamicLabel:
    ({groupTitle}) =>
    ({t, print}) =>
      t(`query-builder.items.frequency-per-dg.name`, {dataGroupName: print(groupTitle)}),
  value: `$frequency`,
  resourceType: ResourceType.Other,
  tooltipData: {
    header: `Frequency per User...`,
    description: ({t}) => t(`query-builder.items.frequency-per-dg.description`),
    entityIcon: `type-custom-number`,
    hideMetricsSection: true,
  },
  menuItemType: MenuItemType.BehaviorWithEvent,
});

export const BEHAVIOR_AGGREGATION: PropertySectionItem<string> = Object.freeze({
  type: PropertyType.Number,
  propertyDefaultType: PropertyType.Number,
  caretStyle: `caret`,
  hasCaret: true,
  hasPill: false,
  icon: `type-custom-number`,
  isDisabled: false,
  label: `Aggregate Property per User...`,
  dynamicLabel:
    ({groupTitle}) =>
    ({t, print}) =>
      t(`query-builder.items.agg-prop-per-dg.name`, {dataGroupName: print(groupTitle)}),
  value: `$aggregation`,
  resourceType: ResourceType.Other,
  tooltipData: {
    header: `Aggregate Property per User...`,
    description: ({t}) => t(`query-builder.items.agg-prop-per-dg.description`),
    entityIcon: `type-custom-number`,
    hideMetricsSection: true,
  },
  menuItemType: MenuItemType.BehaviorWithEvent,
});

export const RECURRING_REVENUE_CHANGE: PropertySectionItem<string> = Object.freeze({
  type: PropertyType.Number,
  propertyDefaultType: PropertyType.Number,
  caretStyle: `caret`,
  hasCaret: false,
  hasPill: false,
  icon: `computed`,
  isDisabled: false,
  label: ({t}) => t(`query-builder.items.recurring-revenue-change.name`),
  value: `$revenue_change`,
  resourceType: ResourceType.Other,
  tooltipData: {
    header: ({t}) => t(`query-builder.items.recurring-revenue-change.name`),
    description: ({t}) => t(`query-builder.items.recurring-revenue-change.description`),
    entityIcon: `computed`,
    hideMetricsSection: true,
  },
  menuItemType: MenuItemType.BehaviorWithProperty,
});

export const AGGREGATE_USER_FREQ_BY_GROUP: PropertySectionItem<string> = Object.freeze({
  type: PropertyType.Number,
  propertyDefaultType: PropertyType.Number,
  caretStyle: `caret`,
  hasCaret: false,
  hasPill: false,
  icon: `computed`,
  isDisabled: false,
  label: ({t}) => t(`query-builder.items.number-of-users-who-did.name`),
  value: `$behavior_aggregation`,
  resourceType: ResourceType.User,
  tooltipData: {
    header: ({t}) => t(`query-builder.items.number-of-users-who-did.name`),
    description: ({t}) => t(`query-builder.items.number-of-users-who-did.description`),
    entityIcon: `computed`,
    hideMetricsSection: true,
  },
  menuItemType: MenuItemType.AggregateUserByGroup,
});

export const RECURRING_REVENUE_CHANGE_TYPE: PropertySectionItem<string> = Object.freeze({
  type: PropertyType.String,
  propertyDefaultType: PropertyType.String,
  caretStyle: `caret`,
  hasCaret: false,
  hasPill: false,
  icon: `computed`,
  isDisabled: false,
  label: ({t}) => t(`query-builder.items.recurring-revenue-change-type.name`),
  value: `$revenue_change_type`,
  resourceType: ResourceType.Other,
  tooltipData: {
    header: ({t}) => t(`query-builder.items.recurring-revenue-change-type.name`),
    description: ({t}) => t(`query-builder.items.recurring-revenue-change-type.description`),
    entityIcon: `computed`,
    hideMetricsSection: true,
  },
  menuItemType: MenuItemType.BehaviorWithProperty,
});

export const BEHAVIOR_MULTI_ATTRIBUTION: PropertySectionItem<string> = Object.freeze({
  type: PropertyType.String,
  propertyDefaultType: PropertyType.String,
  caretStyle: `caret`,
  hasCaret: true,
  hasPill: false,
  icon: `type-custom-text`,
  isDisabled: false,
  label: ({t}) => t(`query-builder.items.attributed-by.name`),
  value: `$multi_attribution`,
  resourceType: ResourceType.Other,
  tooltipData: {
    header: ({t}) => t(`query-builder.items.attributed-by.name`),
    description: ({t}) => t(`query-builder.items.attributed-by.description`),
    entityIcon: `type-custom-text`,
    hideMetricsSection: true,
    // Can flip this to true when we're ready to have the badge displayed post GA
    showNewForFreeUsers: false,
    noteForFreeUsers: `query-builder.items.attributed-by.free-note` as const,
  },
  menuItemType: MenuItemType.BehaviorMultiAttribution, // handles just selection in the properties menu
  stagedItemType: StagedItemType.BehaviorMultiAttribution,
});

export const BEHAVIOR_MULTI_ATTRIBUTION_FILTER: PropertySectionItem<string> = Object.freeze({
  ...BEHAVIOR_MULTI_ATTRIBUTION,
  type: PropertyType.List,
  icon: `type-custom-list`,
  stagedItemType: undefined,
  menuItemType: MenuItemType.BehaviorMultiAttributionFilter,
  value: `$multi_attribution_filter`,
});

export const BORROW_PROPERTY: PropertySectionItem<string> = Object.freeze({
  type: PropertyType.NonProperty,
  propertyDefaultType: PropertyType.NonProperty,
  caretStyle: `caret`,
  hasCaret: true,
  hasPill: false,
  icon: `type-custom-text`,
  isDisabled: false,
  label: `Borrow Property...`,
  value: `$borrow_property`,
  resourceType: ResourceType.Other,
  tooltipData: {
    header: `Borrow Property...`,
    description:
      `Borrow an existing property from a preceding event onto your current event.` +
      ` This functionality allows you to breakdown or filter events by properties that don’t` +
      ` exist on it and try out new analysis. Learn more in our` +
      ` [help docs](https://docs.mixpanel.com/docs/features/custom-properties#borrowed-properties).` +
      `\n\n` +
      `A 7 day lookback window will be applied.`,
    entityIcon: `type-custom-text`,
    hideMetricsSection: true,
  },
  menuItemType: MenuItemType.BorrowProperty,
});

export const ALL_STEPS_FUNNEL_STEP_INDEX_VALUE = null;

export const FUNNELS_TIME_TO_CONVERSION: PropertySectionItem<string> = Object.freeze({
  type: PropertyType.Number,
  propertyDefaultType: PropertyType.Number,
  hasCaret: false,
  hasPill: false,
  icon: `type-custom-number`,
  isDisabled: false,
  label: ({t}) => t(`query-builder.items.time-to-convert.name`),
  value: `$time_to_conversion`,
  startStepNumber: null,
  endStepNumber: null,
  resourceType: ResourceType.Other,
  tooltipData: {
    header: ({t}) => t(`query-builder.items.time-to-convert.name`),
    description: ({t}) => t(`query-builder.items.time-to-convert.description`),
    entityIcon: `type-custom-number`,
    hideMetricsSection: true,
  },
  menuItemType: MenuItemType.Other,
});

export const FLOWS_CONVERSION: PropertySectionItem<string> = Object.freeze({
  type: PropertyType.String,
  propertyDefaultType: PropertyType.String,
  hasCaret: false,
  hasPill: false,
  icon: `top-paths`,
  isDisabled: false,
  label: ({t}) => t(`query-builder.items.conversion.name`),
  value: `$conversion`,
  resourceType: ResourceType.Other,
  tooltipData: {
    header: ({t}) => t(`query-builder.items.conversion.name`),
    description: ({t}) => t(`query-builder.items.conversion.description`),
    entityIcon: `top-paths`,
    extraInfo: [({t}) => t(`query-builder.items.conversion.extra-info`)],
    hideMetricsSection: true,
  },
  menuItemType: MenuItemType.Other,
});

export const FLOWS_CONVERSION_VALUES: string[] = [`Converted`, `Did Not Convert`];

export const PAGE_DURATION: PropertySectionItem<string> = Object.freeze({
  type: PropertyType.Number,
  propertyDefaultType: PropertyType.Number,
  hasCaret: false,
  hasPill: false,
  icon: `type-custom-number`,
  isDisabled: false,
  label: ({t}) => t(`query-builder.items.duration.name`),
  value: `$page_duration`,
  resourceType: ResourceType.Other,
  tooltipData: {
    header: ({t}) => t(`query-builder.items.duration.name`),
    description: ({t}) => t(`query-builder.items.duration.description`),
    entityIcon: `type-custom-number`,
    extraInfo: [],
    hideMetricsSection: true,
  },
  menuItemType: MenuItemType.Other,
});

export const FIRST_EVENT_TIME_PROPERTY: PropertySectionItem<string> = Object.freeze({
  label: ({t}) => t(`query-builder.items.first-seen.name`),
  value: `$mp_first_event_time`,
  propertyDefaultType: PropertyType.Datetime,
  type: PropertyType.Datetime,
  resourceType: ResourceType.User,
  isControlItem: false,
  hasCaret: false,
  hasPill: false,
  icon: `type-date`,
  menuItemType: MenuItemType.Other,
  tooltipData: {
    header: ({t}) => t(`query-builder.items.first-seen.name`),
    description: ({t}) => t(`query-builder.items.first-seen.description`),
    entityIcon: `type-date`,
    hideMetricsSection: true,
  },
});

export const DATASET_PROPERTY: PropertySectionItem<string> = {
  label: ({t}) => t(`query-builder.items.project-name`),
  icon: `star-top-events`,
  value: DATASET_PROPERTY_NAME,
  propertyDefaultType: PropertyType.String,
  type: PropertyType.String,
  resourceType: ResourceType.Event,
  menuItemType: MenuItemType.EventProperties,
};

export const BEHAVIOR_PROPERTIES: Map<string, PropertySectionItem<string>> = new Map([
  [BEHAVIOR_AGGREGATION.value, BEHAVIOR_AGGREGATION],
  [BEHAVIOR_FREQUENCY.value, BEHAVIOR_FREQUENCY],
  [BEHAVIOR_MULTI_ATTRIBUTION.value, BEHAVIOR_MULTI_ATTRIBUTION],
]);

export const SPECIAL_PROPERTIES: Map<string, PropertySectionItem<string>> = new Map([
  [TOP_PEOPLE.value, TOP_PEOPLE],
  [TOP_PATHS.value, TOP_PATHS],
  [FLOWS_CONVERSION.value, FLOWS_CONVERSION],
  [FUNNELS_TIME_TO_CONVERSION.value, FUNNELS_TIME_TO_CONVERSION],
  [BEHAVIOR_AGGREGATION.value, BEHAVIOR_AGGREGATION],
  [BEHAVIOR_FREQUENCY.value, BEHAVIOR_FREQUENCY],
  [BEHAVIOR_MULTI_ATTRIBUTION.value, BEHAVIOR_MULTI_ATTRIBUTION],
  [BORROW_PROPERTY.value, BORROW_PROPERTY],
  [FIRST_EVENT_TIME_PROPERTY.value, FIRST_EVENT_TIME_PROPERTY],
  [DATASET_PROPERTY.value, DATASET_PROPERTY],
  [PAGE_DURATION.value, PAGE_DURATION],
  [RECURRING_REVENUE_CHANGE.value, RECURRING_REVENUE_CHANGE],
  [RECURRING_REVENUE_CHANGE_TYPE.value, RECURRING_REVENUE_CHANGE_TYPE],
]);

export const OTHER_OPTIONS: Map<string, PropertySectionItem<string>> = new Map([
  [TOP_PATHS.value, TOP_PATHS],
  [FLOWS_CONVERSION.value, FLOWS_CONVERSION],
  [FUNNELS_TIME_TO_CONVERSION.value, FUNNELS_TIME_TO_CONVERSION],
  [BEHAVIOR_FREQUENCY.value, BEHAVIOR_FREQUENCY],
  [BEHAVIOR_AGGREGATION.value, BEHAVIOR_AGGREGATION],
  [BEHAVIOR_MULTI_ATTRIBUTION.value, BEHAVIOR_MULTI_ATTRIBUTION],
  [BEHAVIOR_MULTI_ATTRIBUTION_FILTER.value, BEHAVIOR_MULTI_ATTRIBUTION_FILTER],
  [PAGE_DURATION.value, PAGE_DURATION],
  [BORROW_PROPERTY.value, BORROW_PROPERTY],
  [FIRST_EVENT_TIME_PROPERTY.value, FIRST_EVENT_TIME_PROPERTY],
  [RECURRING_REVENUE_CHANGE.value, RECURRING_REVENUE_CHANGE],
  [RECURRING_REVENUE_CHANGE_TYPE.value, RECURRING_REVENUE_CHANGE_TYPE],
  [AGGREGATE_USER_FREQ_BY_GROUP.value, AGGREGATE_USER_FREQ_BY_GROUP],
]);

export const SPECIAL_CONTROLS: Record<string, SpecialControl> = {
  CREATE_INLINE_COHORT: {
    isControlItem: true,
    type: null,
    propertyDefaultType: null,
    icon: `compose`,
    label: `Create Cohort...`,
    resourceType: ResourceType.Cohort,
    value: SpecialActions.CreateInlineCohort,
  },
  CREATE_CUSTOM_EVENT_FORMULA: {
    icon: `compose`,
    isControlItem: true,
    type: null,
    propertyDefaultType: null,
    label: `Create a Custom Property...`,
    resourceType: ResourceType.Event,
    value: SpecialActions.CreateCustomEventFormula,
    tooltipData: {
      header: `Create a Custom Property`,
      description: `• Merge and regroup property values\n• Resize numeric ranges\n• Extract patterns`,
      entityIcon: `compose`,
      helpLink: {
        label: `More on custom properties`,
        href: `https://docs.mixpanel.com/docs/analysis/advanced/custom-properties`,
      },
    },
  },
  CREATE_CUSTOM_PEOPLE_FORMULA: {
    icon: `compose`,
    isControlItem: true,
    type: null,
    propertyDefaultType: null,
    label: `Create a Custom Property...`,
    resourceType: ResourceType.User,
    value: SpecialActions.CreateCustomPeopleFormula,
    tooltipData: {
      header: `Create a Custom Property`,
      description: `• Merge and regroup property values\n• Resize numeric ranges\n• Extract patterns`,
      entityIcon: `compose`,
      helpLink: {
        label: `More on custom properties`,
        href: `https://docs.mixpanel.com/docs/analysis/advanced/custom-properties`,
      },
    },
  },
  CREATE_CUSTOM_RUNTIME_PROPERTY: {
    icon: `time`,
    isControlItem: true,
    type: null,
    propertyDefaultType: null,
    label: `Create a Runtime Property...`,
    resourceType: ResourceType.Event,
    value: SpecialActions.CreateCustomRuntimeProperty,
    tooltipData: {
      header: `Create a Runtime Property`,
      description: `Define properties that are computed at query time based on your raw event data. Common use-cases include: extracting values from existing properties, combining multiple properties, and performing calculations.`,
      entityIcon: `compose`,
    },
  },
  CREATE_CUSTOM_RUNTIME_EVENT: {
    icon: `runtime-event`,
    isControlItem: true,
    type: null,
    propertyDefaultType: null,
    label: `Create a Runtime Event...`,
    resourceType: ResourceType.Event,
    value: SpecialActions.CreateCustomRuntimeEvent,
    tooltipData: {
      header: `Create a Runtime Event`,
      description: `Target users based on when they perform an event for the first time. Common use-cases include: first purchase, first login, or first app open.`,
      entityIcon: `compose`,
    },
  },
  CREATE_CUSTOM_BEHAVIORAL_PROPERTY: {
    icon: `compose`,
    isControlItem: true,
    type: null,
    propertyDefaultType: null,
    label: `Create a Behavior...`,
    resourceType: ResourceType.User,
    value: SpecialActions.CreateCustomBehavioralProperties,
    tooltipData: {
      header: `Create a Behavior`,
      description: `Define properties based on user behavior patterns over time. Common use-cases include: number of times users did an action, summarize user's actions based on a property.`,
      entityIcon: `compose`,
    },
  },
};

export const DATETIME_UNIT_OPTIONS = [
  ...OrderedStandardTimeUnits.filter((unit) => unit !== TimeUnit.Minute),
  ...Object.values(SpecialTimeUnit),
];

export const DATETIME_UNIT_OPTION_SECTION_ITEMS = DATETIME_UNIT_OPTIONS.map((unitValue) => ({
  label: TimeUnitLabelTranslatable[unitValue](1),
  value: unitValue,
}));

export const DURATION_UNIT_OPTIONS = [TimeUnit.Second, ...OrderedStandardTimeUnits];

export const DURATION_UNIT_OPTION_SECTION_ITEMS = DURATION_UNIT_OPTIONS.map((unitValue) => ({
  label: pluralize(TimeUnitLabels[unitValue], true),
  value: unitValue,
}));

export const AllFilter: SectionItem<FilterValue> = {
  label: `all`,
  value: {resourceType: FilterResourceType.All as const, value: AllTab},
};

export const LIST_PROPERTY_SUM: SectionItemProperty<string> = {
  type: PropertyType.Number,
  propertyDefaultType: PropertyType.Number,
  caretStyle: `caret`,
  persistCaret: true,
  hasCaret: true,
  hasPill: false,
  icon: `type-custom-number`,
  isDisabled: false,
  label: ({t}) => t(`query-builder.items.list-sum.name`),
  value: `$list_sum`,
  resourceType: ResourceType.Other,
  tooltipData: {
    header: ({t}) => t(`query-builder.items.list-sum.name`),
    description: ({t}) => t(`query-builder.items.list-sum.description`),
    entityIcon: `type-custom-number`,
    hideMetricsSection: true,
  },
  menuItemType: MenuItemType.ComputedListPropertySum,
  stagedItemType: StagedItemType.ComputedListNumericProperty,
};

export const LIST_PROPERTY_UNIQUES: SectionItemProperty<string> = {
  type: PropertyType.Number,
  propertyDefaultType: PropertyType.Number,
  caretStyle: `caret`,
  persistCaret: true,
  hasCaret: true,
  hasPill: false,
  icon: `type-custom-number`,
  isDisabled: false,
  label: ({t}) => t(`query-builder.items.list-unique.name`),
  value: `$list_unique`,
  resourceType: ResourceType.Other,
  tooltipData: {
    header: ({t}) => t(`query-builder.items.list-unique.name`),
    description: ({t}) => t(`query-builder.items.list-unique.description`),
    entityIcon: `type-custom-number`,
    hideMetricsSection: true,
  },
  menuItemType: MenuItemType.ComputedListPropertyUniques,
  stagedItemType: StagedItemType.ComputedListProperty,
};

export const LIST_LENGTH: SectionItemProperty<string> = {
  type: PropertyType.Number,
  propertyDefaultType: PropertyType.Number,
  icon: `type-custom-number`,
  isDisabled: false,
  label: ({t}) => t(`query-builder.items.list-length.name`),
  value: `$list_length`,
  resourceType: ResourceType.Other,
  tooltipData: {
    header: ({t}) => t(`query-builder.items.list-length.name`),
    description: ({t}) => t(`query-builder.items.list-length.description`),
    entityIcon: `type-custom-number`,
    hideMetricsSection: true,
  },
  menuItemType: MenuItemType.ComputedListLength,
};

export const COMPUTED_LIST_PROPERTIES_VALUES = new Set([
  LIST_LENGTH.value,
  LIST_PROPERTY_SUM.value,
  LIST_PROPERTY_UNIQUES.value,
]);

export const COMPUTED_LIST_PROPERTIES_LABELS = {
  [LIST_LENGTH.value]: `Size`,
  [LIST_PROPERTY_SUM.value]: `Sum`,
  [LIST_PROPERTY_UNIQUES.value]: `Distinct Count`,
};

export const SECOND_WEEK_RETENTION_COHORT: PropertySectionItem<string> = {
  type: null,
  propertyDefaultType: null,
  hasCaret: false,
  hasPill: false,
  isDisabled: false,
  icon: `community`,
  label: ({t}) => t(`signal.retention-2week`),
  value: `$2nd_week`,
  resourceType: ResourceType.Cohort,
  menuItemType: MenuItemType.Cohorts,
};

export const THIRD_WEEK_RETENTION_COHORT: PropertySectionItem<string> = {
  type: null,
  propertyDefaultType: null,
  hasCaret: false,
  hasPill: false,
  isDisabled: false,
  icon: `community`,
  label: ({t}) => t(`signal.retention-3week`),
  value: `$3rd_week`,
  resourceType: ResourceType.Cohort,
  menuItemType: MenuItemType.Cohorts,
};

export const FORTH_WEEK_RETENTION_COHORT: PropertySectionItem<string> = {
  type: null,
  propertyDefaultType: null,
  hasCaret: false,
  hasPill: false,
  isDisabled: false,
  icon: `community`,
  label: ({t}) => t(`signal.retention-4week`),
  value: `$4th_week`,
  resourceType: ResourceType.Cohort,
  menuItemType: MenuItemType.Cohorts,
};

export const SECOND_MONTH_RETENTION_COHORT: PropertySectionItem<string> = {
  type: null,
  propertyDefaultType: null,
  hasCaret: false,
  hasPill: false,
  isDisabled: false,
  icon: `community`,
  label: ({t}) => t(`signal.retention-2month`),
  value: `$2nd_month`,
  resourceType: ResourceType.Cohort,
  menuItemType: MenuItemType.Cohorts,
};

export const ALL_USERS_COHORT: PropertySectionItem<string> = {
  type: null,
  propertyDefaultType: null,
  hasCaret: false,
  hasPill: false,
  isDisabled: false,
  icon: `community`,
  label: ({t}) => t(`signal.cohort-all-users`),
  value: `$all_users`,
  resourceType: ResourceType.Cohort,
  menuItemType: MenuItemType.Cohorts,
};

export const NEW_USERS_COHORT: PropertySectionItem<string> = {
  type: null,
  propertyDefaultType: null,
  hasCaret: false,
  hasPill: false,
  isDisabled: false,
  icon: `community`,
  label: ({t}) => t(`signal.cohort-new-users`),
  value: `$new_users`,
  resourceType: ResourceType.Cohort,
  menuItemType: MenuItemType.Cohorts,
};

export const EXCLUDED_PROPERTY_TYPES_FOR_BORROW_PROP = [PropertyType.List];
